import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import { useToast } from '@/context/ToastContext'
import { useMobiApi } from '@/services/mobi-api'
import { GetEsimDevicesRes } from '@/services/mobi-api/info/types'
import { resolveImageUrl } from '@/utils/constants'
import useIntl from '@/utils/intl'
import { Checkbox, MenuItem, Select, TextField } from '@mui/material'
import Image from 'next/image'
import React, { FunctionComponent, useEffect, useReducer } from 'react'
import classNames from 'classnames'
import { CheckCircle, RadioButtonUncheckedOutlined } from '@mui/icons-material'
import { Obj } from '..'

const ITEM_HEIGHT = 55
const ITEM_PADDING_TOP = 8

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
      backgroundColor: 'var(--color-surface-surfaceInverse)'
    }
  }
}

interface State {
  isClicked?: boolean
  selectedSim?: string
  selectedIdx?: number
  selectedDevice?: string
  data?: GetEsimDevicesRes
  imsi?: string
  loading?: boolean
}

interface Props {
  next: (obj: Obj) => void
  back: () => void
  instanceId: string
}

const ChooseSim: FunctionComponent<Props> = ({ next, back, instanceId }) => {
  const { translate } = useIntl()
  const { info, tourist } = useMobiApi()
  const { showError } = useToast()
  const [state, setState] = useReducer(
    (state: State, newState: State) => ({ ...state, ...newState }),
    {
      isClicked: false
    }
  )
  useEffect(() => { void fetchData() }, [])
  const fetchData = async (): Promise<void> => {
    try {
      const res = await info.getEsimDevices()
      setState({ data: res })
    } catch (e) {
      console.error(e)
      showError(translate('error'))
    }
  }

  const categories = [
    {
      title: 'eSIM',
      desc: translate('tourist.esim.desc')
    },
    {
      title: translate('tourist.selfsim'),
      desc: translate('tourist.selfsim.subtitle')
    }
  ]

  const handleChangeImsi = (value: string): void => {
    if (value.length < 10) {
      setState({ imsi: value })
    }
  }

  const handleChooseSim = async (): Promise<void> => {
    try {
      let res
      if (state.selectedIdx === 1) {
        res = await tourist.touristSaveSelfSim(state.imsi ?? '')
      } else {
        res = await tourist.touristSaveEsim()
      }
      if (res?.error !== undefined) {
        showError(res?.cause?.messageEn ?? res?.error ?? translate('tourist.request.failed'))
        // next({ choosenSim: 'SELFSIM', selfsimcode: '8997699120616393113F', imsi: '616393113' })
      } else {
        if (state.selectedIdx === 1 && res?.imsi !== undefined) {
          next({ choosenSim: 'SELFSIM', selfsimcode: res?.imsi, imsi: state.imsi ?? undefined })
        } else if (state.selectedIdx !== 1 && res?.qr_string !== undefined) {
          next({ choosenSim: 'ESIM', imsiQr: res?.qr_string ?? undefined, selfsimcode: res?.imsi ?? undefined })
        } else {
          translate('tourist.request.failed')
        }
      }
    } catch (e) {
      translate('tourist.request.failed')
    } finally {
      setState({ loading: false })
    }
  }

  return (
    <div className='bg-surfaceMid rounded-xl p-6 md:p-8 flex flex-col items-start justify-center gap-4'>
      {categories.map((item, i) => (
        <div
          key={i}
          className={classNames('p-6 rounded-2xl border w-full flex flex-col cursor-pointer bg-surfaceInverse', item.title === state.selectedSim ? 'border-secondaryMainStroke' : 'border-secondaryMidStroke')}
          onClick={() => setState({ selectedSim: item.title, isClicked: true, selectedIdx: i, imsi: undefined })}
        >
          <div className='flex justify-between items-start '>
            <div className='flex flex-col'>
              <span className='font-bold text-textHigh'>{item.title}</span>
              <span className='text-textMid text-sm'>{item.desc}</span>
            </div>
            <Checkbox
              color='error' icon={<RadioButtonUncheckedOutlined className='text-textHigh' />}
              checkedIcon={<CheckCircle className='text-primaryMain' />} className='p-0' checked={item.title === state.selectedSim}
            />
          </div>
          {
            state.selectedIdx === i
              ? (state.selectedIdx === 1
                  ? (
                    <div className='flex flex-col gap-4 mt-12'>
                      <TextField
                      // value={simNum}
                        type='number'
                        label='***********F'
                        variant='outlined'
                        className='w-full rounded-lg border-secondaryHighStroke bg-surfaceInverse text-textMid'
                        onChange={(e) => { handleChangeImsi(e.target.value) }}
                        inputProps={{ inputMode: 'numeric' }}
                        InputLabelProps={{
                          style: { color: 'var(--color-text-textHigh)' }
                        }}
                        InputProps={{
                          style: {
                            color: 'var(--color-text-textHigh)',
                            '&::placeholder': {
                              color: 'var(--color-text-secondaryHighStroke)'
                            }
                          } as any
                        }}
                        sx={{
                          '& fieldset': {
                            borderRadius: '8px'
                          }
                        }}
                      />
                      <div className='flex p-3 gap-5 w-full'>
                        <Image draggable={false} width={1000} height={500} src={resolveImageUrl('/uploads/icons/selfSim.png')} alt='selfSim' className='w-2/5' />
                        <p className='text-textMid text-sm font-medium h-fit'>{translate('tourist.selfsim.desc')}</p>

                      </div>
                      <div className='flex items-start gap-3 border border-[#F4A610] bg-[#FEFCF5] rounded-lg p-3 mt-4'>
                        <Image draggable={false} width={30} height={30} src={resolveImageUrl('/uploads/icons/info.svg')} alt='!' className='border-2 border-[#FEF3C7] rounded-full' />
                        <div className='flex flex-col gap-2'>
                          <span className='text-sm text-[#854D0E] font-medium'>{translate('tourist.selfsim.warning')}</span>
                        </div>
                      </div>
                    </div>
                    )
                  : (
                    <div className='mt-12 gap-3'>
                      <span className='text-textHigh font-bold text-sm'>{translate('tourist.esim.phonelist')}</span>
                      <Select
                        className='rounded-xl border-secondaryHighStroke bg-surfaceInverse w-full text-sm text-textHigh mt-3'
                        MenuProps={MenuProps}
                        value={state.selectedDevice ?? ''}
                      >
                        <MenuItem className='bg-surfaceInverse text-textHigh'>{translate('tourist.esim.choose')}</MenuItem>
                        {state.data?.devices.map((item, i) => (
                          <MenuItem className='bg-surfaceInverse text-textHigh' key={i} value={item} onClick={() => setState({ selectedDevice: item })}>{item}</MenuItem>
                        ))}
                      </Select>
                      <div className='flex items-start gap-3 border border-[#F4A610] bg-[#FEFCF5] rounded-lg p-3 mt-4'>
                        <Image draggable={false} width={30} height={30} src={resolveImageUrl('/uploads/icons/info.svg')} alt='!' className='border-2 border-[#FEF3C7] rounded-full' />
                        <div className='flex flex-col gap-2'>
                          <span className='text-sm text-[#854D0E] font-medium'>{translate('tourist.esim.info1')}</span>
                          <span className='text-sm text-[#854D0E] font-bold'>{translate('tourist.esim.info2')}</span>
                        </div>
                      </div>
                    </div>
                    )
                )
              : <></>
            }
        </div>

      ))}
      <div className='w-full flex justify-end'>
        <DarkButton onClick={() => { void handleChooseSim() }} disabled={!(state.isClicked === true && (state.selectedIdx === 1 ? (state.imsi !== undefined && state.imsi !== null && state.imsi.length === 9) : (state.selectedDevice !== undefined && state.selectedDevice !== null)))}>{translate('tourist.select')}</DarkButton>
      </div>
    </div>
  )
}
export default ChooseSim
